import { MouseEvent } from "react";
import { MenuItem } from "@headlessui/react";
import { RiMoreFill } from "@remixicon/react";

import Menu, { MenuButton, MenuItems } from "components/Menu";
import RotatingArrowIcon from "components/RotatingArrowIcon";
import { cx } from "utils";

export type ActionMenuItem = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
};

interface ActionsMenuProps {
  items: ActionMenuItem[];
  ariaLabel?: string;
  triggerClassName?: string;
  label?: string;
  disabled?: boolean;
}

// Table actions menu: three dots, or a labelled dropdown
export default function ActionsMenu({
  items,
  ariaLabel = "Actions",
  triggerClassName,
  label,
  disabled,
}: ActionsMenuProps) {
  return (
    <Menu>
      <MenuButton
        aria-label={label ? undefined : ariaLabel}
        // Rows are clickable; don't let opening the menu trigger the row.
        onClick={(e: MouseEvent) => e.stopPropagation()}
        disabled={disabled}
        className={cx(
          label
            ? "select select-button flex items-center justify-between h-10 px-2 gap-x-2 text-sm rounded"
            : "p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          triggerClassName
        )}
      >
        {({ open }) => label ? (
          <>
            {label}
            <RotatingArrowIcon direction="down" rotated={open} className="size-4 text-gray-600 dark:text-neutral-500" />
          </>
        ) : (
          <RiMoreFill className="w-6 h-6" />
        )}
      </MenuButton>
      <MenuItems
        anchor={{ to: "bottom end", gap: "0.25rem" }}
        className="min-w-[160px] rounded-md z-[10000]"
      >
        {items.map((item) => (
          <MenuItem key={item.label} disabled={item.disabled}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                item.onClick();
              }}
              disabled={item.disabled}
              className={cx(
                "block w-full text-left px-4 py-2 text-sm data-[focus]:bg-gray-100",
                "disabled:text-gray-400 disabled:cursor-not-allowed",
                item.destructive && !item.disabled && "text-red-600"
              )}
            >
              {item.label}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}
